import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PixelFactoryContext, PixelTable } from 'pixel-base'
import {
  allocatePayment,
  deleteFinancePayment,
  getFinanceInvoices,
  getPayment,
  getVendorBills,
  postPaymentDocument,
  reversePaymentDocument,
} from 'utils/api-routes-accounts'
import AllocationWorkbench from '../../components/AllocationWorkbench'
import FinanceDocumentActions from '../../components/domain/FinanceDocumentActions'
import FinanceDocumentSummary, {
  asStatusNode,
} from '../../components/domain/FinanceDocumentSummary'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceCard from '../../components/foundation/FinanceCard'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { Invoice, Payment } from '../../types/finance'
import formatFinanceDate from '../../utils/formatFinanceDate'

const PaymentDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const payment = useFinanceObservable<Payment>(() => getPayment(id), {
    immediate: Boolean(id),
  })
  const paymentRow = payment.data
  const paymentState = (paymentRow?.state || 'draft').toLowerCase()

  const candidates = useFinanceObservable<Invoice[]>(
    () =>
      paymentRow?.payment_direction === 'outbound'
        ? getVendorBills({ partner_id: paymentRow?.partner_id })
        : getFinanceInvoices({ partner_id: paymentRow?.partner_id }),
    { immediate: Boolean(paymentRow?.partner_id) },
  )

  const candidateRows = React.useMemo(
    () =>
      (candidates.data || []).filter(
        (row) =>
          Number(row.amount_residual || 0) > 0 &&
          !['reversed', 'cancelled'].includes((row.state || '').toLowerCase()),
      ),
    [candidates.data],
  )

  const runAction = (request, successMessage: string, goBack = false) => {
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(successMessage)
        if (goBack) navigate('/finance/payments')
        else {
          payment.refresh()
          candidates.refresh()
        }
      },
      error: () => pixelFactory?.showFailure?.('Payment action failed'),
    })
  }

  const allocate = (allocations) => {
    runAction(
      allocatePayment(paymentRow.id, { allocations }),
      'Payment allocated',
    )
  }

  return (
    <FinancePageShell
      title={paymentRow?.number || `Payment #${id}`}
      breadcrumbs={[
        { name: 'Finance', active: false },
        { name: 'Payments', active: false },
        { name: paymentRow?.number || `Payment #${id}`, active: true },
      ]}
      isLoading={payment.loading || candidates.loading}
    >
      {paymentRow && (
        <div style={{ display: 'grid', gap: 20 }}>
          <FinanceDocumentSummary
            title="Payment Summary"
            status={asStatusNode(paymentRow.state)}
            fields={[
              { label: 'Direction', value: paymentRow.payment_direction },
              {
                label: 'Partner',
                value:
                  paymentRow.partner?.name || paymentRow.partner_id || '--',
              },
              {
                label: 'Journal',
                value:
                  paymentRow.journal?.name || paymentRow.journal_id || '--',
              },
              {
                label: 'Payment Date',
                value: formatFinanceDate(paymentRow.payment_date),
              },
              {
                label: 'Amount',
                value: paymentRow.amount ?? '--',
                emphasis: true,
              },
              {
                label: 'Open Amount',
                value: paymentRow.open_amount ?? '--',
                emphasis: true,
              },
              {
                label: 'Counterparty Account',
                value: paymentRow.counterparty_account_id || '--',
              },
              {
                label: 'Posted Move',
                value: paymentRow.journal_entry_id || '--',
              },
            ]}
          />
          <FinanceCard title="Allocations">
            <PixelTable
              columns={[
                {
                  name: 'Invoice ID',
                  selector: (row) => row.invoice_id || '--',
                },
                {
                  name: 'Allocated Amount',
                  selector: (row) => row.allocated_amount || '--',
                },
                {
                  name: 'Allocation Date',
                  selector: (row) => formatFinanceDate(row.allocation_date),
                },
                {
                  name: 'Reconciliation',
                  selector: (row) => row.reconciliation_id || '--',
                },
              ]}
              data={paymentRow.allocations || []}
              pagination
            />
          </FinanceCard>
          {paymentState === 'posted' &&
            Number(paymentRow.open_amount || paymentRow.amount || 0) > 0 && (
              <AllocationWorkbench
                title="Allocate Payment"
                rows={candidateRows}
                availableAmount={Number(
                  paymentRow.open_amount || paymentRow.amount || 0,
                )}
                onSubmit={allocate}
              />
            )}
          <FinanceDocumentActions
            actions={[
              {
                key: 'post',
                label: 'Post',
                tone: 'success',
                hidden: paymentState !== 'draft',
                onClick: () =>
                  runAction(
                    postPaymentDocument(paymentRow.id, {
                      idempotency_key: `payment-post-${paymentRow.id}`,
                    }),
                    'Payment posted',
                  ),
              },
              {
                key: 'reverse',
                label: 'Reverse',
                tone: 'warning',
                hidden: paymentState !== 'posted',
                onClick: () =>
                  runAction(
                    reversePaymentDocument(paymentRow.id, {
                      idempotency_key: `payment-reverse-${paymentRow.id}`,
                    }),
                    'Payment reversed',
                  ),
              },
              {
                key: 'delete',
                label: 'Delete',
                tone: 'danger',
                hidden: paymentState !== 'draft',
                onClick: () =>
                  runAction(
                    deleteFinancePayment(paymentRow.id),
                    'Payment deleted',
                    true,
                  ),
              },
            ]}
          />
        </div>
      )}
    </FinancePageShell>
  )
}

export default PaymentDetail
